import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Brand } from '../../interfaces/brand';

@Injectable({ providedIn: 'root' })
export class BrandService {
  private brand: Brand | null = null;

  constructor(private http: HttpClient) {}

  get logo(): string { return this.brand?.logo ?? ''; }
  get brandName(): string { return this.brand?.brandName ?? ''; }
  get primaryColor(): string { return this.brand?.primaryColor ?? '#8B45BC'; }
  get secondaryColor(): string { return this.brand?.secondaryColor ?? '#00AC70'; }
  get accentColor(): string { return this.brand?.additionalData?.['accentColor'] ?? this.brand?.primaryColor ?? '#402870'; }
  get sidebarBgColor(): string { return this.brand?.additionalData?.['sidebarBg'] ?? '#101820'; }
  get menuBgColor(): string { return this.brand?.additionalData?.['menuBg'] ?? '#ffffff'; }
  get loginCardBgColor(): string { return this.brand?.additionalData?.['loginCardBg'] ?? '#ffffff'; }
  get loginCardText(): string {
    return this.brand?.additionalData?.['loginCardText']
        ?? this.brand?.additionalData?.['loginTextColor']
        ?? '#111827';
  }
  get footerTagline(): string {
    return this.brand?.additionalData?.['footerTagline']
        ?? this.brand?.additionalData?.['footerText']
        ?? 'healthcare platform';
  }
  get footerLogo(): string { return this.brand?.additionalData?.['footerLogo'] || this.brand?.logo || 'logo-footer.png'; }
  // Padrão = Clínica Sim (empresa 2): se a brand do domínio não trouxer cdEmpresa,
  // assume empresa 2 em vez de deixar o login sem unidades.
  get cdEmpresa(): string { return this.brand?.additionalData?.['cdEmpresa'] ?? '2'; }
  // Nome do plano de assinatura e da clinica por empresa: SiM+ / Clinica SiM na SiM (2),
  // Super Vida / Super Clinica na Super Clinica (16).
  get nomePlano(): string { return this.cdEmpresa === '16' ? 'Super Vida' : 'SiM+'; }
  get nomeMarca(): string { return this.brandName || (this.cdEmpresa === '16' ? 'Super Clínica' : 'Clínica SiM'); }
  get estabelecimentosIds(): number[] {
    const raw = this.brand?.additionalData?.['estabelecimentosIds'];
    if (!raw) return [];
    try { return JSON.parse(raw) as number[]; }
    catch { return []; }
  }
  // Identidade visual na tela (ecossistema Flua). Logos e cor de acao ficam no codigo ate o
  // Brand Manager ter os campos; o `logo` acima segue alimentando os comprovantes impressos.
  get ehSuper(): boolean { return (this.marcaForcada ?? this.cdEmpresa) === '16'; }
  get logoTela(): string { return this.ehSuper ? 'marca/superclinica-logo.png' : 'marca/clinicasim-logo.png'; }
  get logoTelaNegativo(): string { return this.ehSuper ? 'marca/superclinica-logo-negativo.png' : 'marca/clinicasim-logo.png'; }
  get nomeMarcaTela(): string { return this.ehSuper ? 'Super Clínica' : 'Clínica SiM'; }

  // Fora de producao, ?marca=super|sim troca so o visual, para homologar as duas marcas.
  private readonly marcaForcada: string | null = (() => {
    if (environment.production) return null;
    const marca = new URLSearchParams(window.location.search).get('marca');
    if (marca) { try { sessionStorage.setItem('fila:marca', marca); } catch { /* sem storage */ } }
    let salva: string | null = null;
    try { salva = sessionStorage.getItem('fila:marca'); } catch { /* sem storage */ }
    return salva === 'super' ? '16' : salva === 'sim' ? '2' : null;
  })();

  // Telefone do suporte no rodape do login; sem cadastro na marca, o item some.
  get suporteTelefone(): string { return this.brand?.additionalData?.['supportPhone'] ?? ''; }
  get footerText(): string { return this.brand?.additionalData?.['footerText'] ?? 'healthcare platform'; }
  get footerCopyright(): string { return this.brand?.additionalData?.['footerCopyright'] ?? 'copyright © SiMCo. All Rights Reserved'; }

  async loadBrand(): Promise<void> {
    // ?marca=super fora de producao carrega a brand real da Super (empresa 16, unidades, nomes).
    const domain = this.marcaForcada === '16' ? 'filainteligente.superclinica.com.br' : window.location.hostname;
    try {
      this.brand = await firstValueFrom(
        this.http.get<Brand>(`${environment.brandApiUrl}/api/brand/${domain}`)
      );
      this.applyBrand();
    } catch {
      // Domínio não cadastrado no brand-manager — mantém visuais padrão
    }
    this.aplicarMarca();
  }

  // As cores da marca no Brand Manager sao da identidade anterior: na tela vale a paleta Flua
  // com a acao regional (SiM verde escurecido para contraste AA, Super navy), definida no
  // styles.scss pela classe marca-super para o modo escuro poder ajustar.
  private aplicarMarca(): void {
    document.documentElement.classList.toggle('marca-super', this.ehSuper);
  }

  private applyBrand(): void {
    if (!this.brand) return;
    const pageTitle = this.brand.additionalData?.['pageTitle'];
    if (pageTitle) document.title = pageTitle;

    const faviconUrl = this.brand.additionalData?.['favicon'];
    if (faviconUrl) this.applyFavicon(faviconUrl);
  }

  private applyFavicon(url: string): void {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = url;
  }
}
